<template>
  <div class="w-full">
    <!-- Hero -->
    <div
      :class="isDarkMode ? 'bg-truegray-700 text-white' : 'bg-white text-black'"
      class="flex flex-col h-120 w-full relative z-10 pb-60 overflow-hidden"
    >
      <!-- Border line -->
      <div
        :class="isDarkMode ? 'bg-white' : 'bg-truegray-300'"
        class="post-intro"
      ></div>
      <div class="ml-4 lg:ml-16 mt-32">
        <h3 class="mt-4 lg:mt-0">{{ settings.profile_title }} Newsletters</h3>
        <h5 class="mb-8">
          Published:
          <span v-if="newsletterData">{{
            newsletterData.publishedAtForUi
          }}</span>
        </h5>

        <h5
          class="items-center uppercase mt-auto cursor-pointer flex"
          @click="goBack"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-5 w-5 mr-2"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M7 16l-4-4m0 0l4-4m-4 4h18"
            />
          </svg>
          <span>Back</span>
        </h5>
      </div>
    </div>

    <div
      class="bg-truegray-100 w-full pb-16 flex flex-col items-center lg:items-start"
    >
      <!-- Post content -->
      <div
        class="lg:min-h-post overflow-hidden rounded-xl lg:ml-16 -mt-48 relative z-20 py-16 px-6 lg:p-16 flex w-11/12 lg:w-9/12 transition-all duration-500"
        :class="[
          loaded
            ? 'bg-truegray-300'
            : 'bg-truegray-500 items-center justify-center',
          !isDarkMode ? 'border-2 border-truegray-100' : '',
        ]"
      >
        <div
          v-if="!loaded"
          class="h-48 w-48 absolute right-1/2 bottom-1/2 transform translate-x-1/2 translate-y-1/2"
        >
          <lottie :options="loadingAnimationOptions" />
        </div>

        <div v-else class="w-full">
          <NewsletterBuilder v-if="newsletterData" :config="config" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, computed, ref, onMounted } from 'vue'
import { useStore } from '../../../../js/store/index.js'
import { useRouter } from 'vue-router/composables'
import { EventBus } from '../../../../js/plugins/event-bus.js'
import NewsletterBuilder from '../../manager/pages/mail/NewsletterBuilder.vue'
import * as loadingAnimation from '../../../../shared/components/loading.json'

export default defineComponent({
  name: 'PublicNewsletter',

  components: {
    NewsletterBuilder,
  },

  props: {
    newsletterId: {
      type: Number,
      required: true,
    },
  },

  setup(props) {
    const store = useStore()
    const router = useRouter()

    const settings = computed(() => store.getters['shared/getSettings'])
    const customisation = computed(
      () => store.getters['careerwise/getCustomisation'],
    )
    const isDarkMode = computed(() => store.getters['careerwise/isDarkMode'])

    const loadingAnimationOptions = {
      animationData: loadingAnimation.default,
    }
    const loaded = ref(false)

    const newsletterData = ref({})

    const goBack = () => {
      router.push({ path: '/' })
    }

    const throwError = () => {
      const alert = {
        status: 'error',
        message: 'Newsletter not found',
      }
      EventBus.$emit('triggerAlertResponse', alert)
      goBack()
    }

    const getNewsletter = async () => {
      const response = await store.dispatch(
        'careerwise/dispatchGetPublicNewsletter',
        props.newsletterId,
      )

      if (response && response?.data?.success && response?.data?.newsletter) {
        newsletterData.value = response?.data?.newsletter
        const isSent = newsletterData.value.sent_at
        const isPublished = JSON.parse(
          newsletterData.value.information,
        )?.published_without_email
        if (!isSent && !isPublished) {
          // This is a draft. published_without_email gets saved for unemailed published newsletters
          throwError()
        }
        loaded.value = true
      } else {
        throwError()
      }
    }

    const config = computed(() => {
      return {
        build: false,
        presetData: {
          newsletterTitle: newsletterData.value.title,
          newsletterBlurb: newsletterData.value.blurb,
          newsletterPosts: newsletterData.value.postsForUi,
        },
      }
    })

    onMounted(() => {
      getNewsletter()
    })

    return {
      settings,
      customisation,
      isDarkMode,
      loaded,
      loadingAnimationOptions,
      getNewsletter,
      newsletterData,
      config,
      goBack,
    }
  },
})
</script>

<style scoped>
.post-intro {
  height: 1px;
  width: 500px;
  min-width: 95%;
  position: absolute;
  top: 4rem;
  right: 0;
}
</style>
